import { CommissionStatuses } from 'constants/commissionStatuses';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { useCheckFinanceAccess } from 'hooks/useCheckFinanceAccess';
import { useCheckPaymentAccess } from 'hooks/useCheckPaymentAccess';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { RecordContextProvider, useGetList, useNotify, useRecordContext } from 'react-admin';
import { TypographyWithBold } from 'shared/mui/Typography';
import { Commission } from 'types/embedded/commission';
import {
  CommissionRecalculationTask,
  CommissionRecalculationTaskStatus,
} from 'types/embedded/commissionRecalculationTask';
import { getLastUrlSegment } from 'utils';

import { ContainerStyled } from './CommissionsTabsTask.styled';
import { CommissionsTabsTaskCreate } from './CommissionsTabsTaskCreate/CommissionsTabsTaskCreate';
import { CommissionsTabsTaskCreateDialog } from './CommissionsTabsTaskCreate/CommissionsTabsTaskCreateDialog/CommissionsTabsTaskCreateDialog';
import { CommissionsTabsTaskCreateSuccessDialog } from './CommissionsTabsTaskCreate/CommissionsTabsTaskCreateSuccessDialog/CommissionsTabsTaskCreateSuccessDialog';
import { CommissionsTabsTaskLaunch } from './CommissionsTabsTaskLaunch/CommissionsTabsTaskLaunch';
import { CommissionsTabsTaskNotExist } from './CommissionsTabsTaskNotExist/CommissionsTabsTaskNotExist';
import { CommissionsTabsTaskSkeleton } from './CommissionsTabsTaskSkeleton';
import { CommissionsTabsTaskStatus } from './CommissionsTabsTaskStatus/CommissionsTabsTaskStatus';
import { CommissionsTabsTaskShowDialog } from './components/CommissionsTabsTaskShowDialog/CommissionsTabsTaskShowDialog';
import { commissionTaskAccess } from './utils/commissionTaskAccess';

export const CommissionsTabsTask: React.FC = () => {
  const translate = useTypedTranslate();
  const notify = useNotify();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const record = useRecordContext<Commission>();

  const { isHavePaymentAccess } = useCheckPaymentAccess();
  const { isHaveFinanceAccess } = useCheckFinanceAccess();
  const isRoleAccess = isHavePaymentAccess || isHaveFinanceAccess;
  const commissionStatus = record?.status?.value as CommissionStatuses;

  const canCreate = commissionTaskAccess.canCreate(commissionStatus);

  const { data: tasks, isLoading } = useGetList<CommissionRecalculationTask>(
    Resources.CommissionRecalculationTasks,
    {
      filter: { commission: record?.id },
    },
  );

  const {
    handleCloseRecordModal: handleCloseSuccessModal,
    openRecordModal: openSuccessModal,
    record: recordSuccess,
    handleOpenRecordModal: handleOpenSuccessModal,
  } = useRecordModal<CommissionRecalculationTask>();

  const {
    handleCloseRecordModal: handleCloseShowModal,
    record: recordShow,
    openRecordModal: openShowModal,
    handleOpenRecordModal: handleOpenShowModal,
  } = useRecordModal<CommissionRecalculationTask>();

  const onSuccess = async (data: CommissionRecalculationTask) => {
    setOpenCreateDialog(false);
    handleOpenSuccessModal(data);
    notify(
      <TypographyWithBold
        text={translate('app.commissionsRecalculation.createDialog.success', {
          name: data?.commission?.name,
        })}
      />,
      { type: 'success' },
    );
    await queryClient.invalidateQueries();
  };

  if (isLoading) {
    return <CommissionsTabsTaskSkeleton />;
  }

  if (tasks && tasks.length > 0) {
    return (
      <ContainerStyled>
        {tasks.map((task) => {
          const taskStatusSegment = getLastUrlSegment(task?.status?.['@id']);

          const showLaunch =
            [CommissionStatuses.Approved, CommissionStatuses.Implemented].includes(
              commissionStatus,
            ) &&
            taskStatusSegment === CommissionRecalculationTaskStatus.Created &&
            isHaveFinanceAccess;

          return (
            <RecordContextProvider key={task.id} value={task}>
              {showLaunch ? (
                <CommissionsTabsTaskLaunch key={task.id} />
              ) : (
                <CommissionsTabsTaskStatus commissionStatus={commissionStatus} />
              )}
            </RecordContextProvider>
          );
        })}
        <CommissionsTabsTaskCreateSuccessDialog
          onClose={handleCloseSuccessModal}
          onShow={(record) => {
            handleOpenShowModal(record);
            handleCloseSuccessModal();
          }}
          open={openSuccessModal}
          record={recordSuccess}
        />

        <CommissionsTabsTaskShowDialog
          onClose={handleCloseShowModal}
          open={openShowModal}
          record={recordShow}
        />
      </ContainerStyled>
    );
  }

  if (isRoleAccess && canCreate) {
    return (
      <>
        <CommissionsTabsTaskCreate onCreate={() => setOpenCreateDialog(true)} />
        <CommissionsTabsTaskCreateDialog
          onClose={() => setOpenCreateDialog(false)}
          onSuccess={onSuccess}
          open={openCreateDialog}
        />
      </>
    );
  }

  return <CommissionsTabsTaskNotExist />;
};
